import { useReducer } from "react";
import { PlaygroundType } from "@/services/api/widget/types";
import type { StatisticType } from "@/components/WidgetConfig";
import type { StatsViewColumn } from "@/services/api/statistics/types";

// Default columns for statistics table - ordered list of columns to display
const DEFAULT_COLUMNS: StatsViewColumn[] = [
  { type: "period_return", period: "mtd" },
  { type: "period_return", period: "ytd" },
  { type: "statistic", field: "cumulative_performance", lookback: "1y" },
  { type: "statistic", field: "annualized_performance", lookback: "3y" },
  { type: "statistic", field: "annualized_performance", lookback: "5y" },
  { type: "statistic", field: "volatility", frequency: "monthly", lookback: "5y" },
  { type: "statistic", field: "correlation", frequency: "monthly", lookback: "si" },
];

export type ChartSettings = {
  // Period settings
  period: string;
  customDateRange: [string, string];

  // Display settings
  displayMode: "index" | "performance";
  rebaseValue: number;
  frequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

  // Statistic settings
  statistic: StatisticType;
  windowSize: number;
  metricFieldPath: string;

  // Exposure settings
  exposureType: "asset_allocation" | "sector" | "geography" | "currency";
  combineVariants: boolean;
  exposureDisplayMode: "weights" | "scaled_weights" | "values";

  // Scatter chart settings
  xStatistic: string;
  yStatistic: string;
  xLabel: string;
  yLabel: string;

  // Statistics table settings - single ordered columns array
  columns: StatsViewColumn[];
  showBenchmarks: boolean;
  /** Extend incomplete product performance at window edges using proxy benchmark */
  extendIncompleteWithProxy: boolean;
  /** Annual risk-free rate for Sharpe/Sortino calculations (decimal, e.g., 0.02 for 2%) */
  riskFreeRate: number;

  // Legend settings
  legendMode?: "stacked" | "inline";
  legendPosition?: "right" | "bottom";
};

type SettingsAction =
  | { type: "SET_PERIOD"; payload: string }
  | { type: "SET_CUSTOM_DATE_RANGE"; payload: [string, string] }
  | { type: "SET_DISPLAY_MODE"; payload: "index" | "performance" }
  | { type: "SET_REBASE_VALUE"; payload: number }
  | {
      type: "SET_FREQUENCY";
      payload: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
    }
  | { type: "SET_STATISTIC"; payload: StatisticType }
  | { type: "SET_WINDOW_SIZE"; payload: number }
  | { type: "SET_METRIC_FIELD_PATH"; payload: string }
  | {
      type: "SET_EXPOSURE_TYPE";
      payload: "asset_allocation" | "sector" | "geography" | "currency";
    }
  | { type: "SET_COMBINE_VARIANTS"; payload: boolean }
  | {
      type: "SET_EXPOSURE_DISPLAY_MODE";
      payload: "weights" | "scaled_weights" | "values";
    }
  | { type: "SET_X_STATISTIC"; payload: string }
  | { type: "SET_Y_STATISTIC"; payload: string }
  | { type: "SET_X_LABEL"; payload: string }
  | { type: "SET_Y_LABEL"; payload: string }
  | { type: "SET_COLUMNS"; payload: StatsViewColumn[] }
  | { type: "SET_SHOW_BENCHMARKS"; payload: boolean }
  | { type: "SET_EXTEND_INCOMPLETE_WITH_PROXY"; payload: boolean }
  | { type: "SET_RISK_FREE_RATE"; payload: number }
  | { type: "SET_MULTIPLE"; payload: Partial<ChartSettings> };

// Default chart settings
const defaultSettings: ChartSettings = {
  period: "3y",
  customDateRange: ["", ""],
  displayMode: "performance",
  rebaseValue: 100,
  frequency: "monthly",
  statistic: "volatility",
  windowSize: 30,
  metricFieldPath:
    "calculated_fields.statistics.saltus-stat-volatility-3y-m-ex-ante.value",
  exposureType: "asset_allocation",
  combineVariants: true,
  exposureDisplayMode: "scaled_weights",
  xStatistic: "generic-stat-annualized-3y",
  yStatistic: "generic-stat-volatility-36m-m",
  xLabel: "3y Annualised Performance",
  yLabel: "3y Annualised Volatility",
  columns: DEFAULT_COLUMNS,
  showBenchmarks: false,
  extendIncompleteWithProxy: false,
  riskFreeRate: 0.02,
};

// Settings reducer
const settingsReducer = (
  state: ChartSettings,
  action: SettingsAction
): ChartSettings => {
  switch (action.type) {
    case "SET_PERIOD":
      return { ...state, period: action.payload };
    case "SET_CUSTOM_DATE_RANGE":
      return { ...state, customDateRange: action.payload };
    case "SET_DISPLAY_MODE":
      return { ...state, displayMode: action.payload };
    case "SET_REBASE_VALUE":
      return { ...state, rebaseValue: action.payload };
    case "SET_FREQUENCY":
      return { ...state, frequency: action.payload };
    case "SET_STATISTIC":
      return { ...state, statistic: action.payload };
    case "SET_WINDOW_SIZE":
      return { ...state, windowSize: action.payload };
    case "SET_METRIC_FIELD_PATH":
      return { ...state, metricFieldPath: action.payload };
    case "SET_EXPOSURE_TYPE":
      return { ...state, exposureType: action.payload };
    case "SET_COMBINE_VARIANTS":
      return { ...state, combineVariants: action.payload };
    case "SET_EXPOSURE_DISPLAY_MODE":
      return { ...state, exposureDisplayMode: action.payload };
    case "SET_X_STATISTIC":
      return { ...state, xStatistic: action.payload };
    case "SET_Y_STATISTIC":
      return { ...state, yStatistic: action.payload };
    case "SET_X_LABEL":
      return { ...state, xLabel: action.payload };
    case "SET_Y_LABEL":
      return { ...state, yLabel: action.payload };
    case "SET_COLUMNS":
      return { ...state, columns: action.payload };
    case "SET_SHOW_BENCHMARKS":
      return { ...state, showBenchmarks: action.payload };
    case "SET_EXTEND_INCOMPLETE_WITH_PROXY":
      return { ...state, extendIncompleteWithProxy: action.payload };
    case "SET_RISK_FREE_RATE":
      return { ...state, riskFreeRate: action.payload };
    case "SET_MULTIPLE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const usePlaygroundSettings = (
  initialSettings?: Partial<ChartSettings>
) => {
  const [settings, dispatch] = useReducer(settingsReducer, {
    ...defaultSettings,
    ...initialSettings,
  });

  // Action dispatchers
  const setPeriod = (period: string) =>
    dispatch({ type: "SET_PERIOD", payload: period });
  const setCustomDateRange = (range: [string, string]) =>
    dispatch({ type: "SET_CUSTOM_DATE_RANGE", payload: range });
  const setDisplayMode = (mode: "index" | "performance") =>
    dispatch({ type: "SET_DISPLAY_MODE", payload: mode });
  const setRebaseValue = (value: number) =>
    dispatch({ type: "SET_REBASE_VALUE", payload: value });
  const setFrequency = (
    freq: "daily" | "weekly" | "monthly" | "quarterly" | "yearly"
  ) => dispatch({ type: "SET_FREQUENCY", payload: freq });
  const setStatistic = (stat: StatisticType) =>
    dispatch({ type: "SET_STATISTIC", payload: stat });
  const setWindowSize = (size: number) =>
    dispatch({ type: "SET_WINDOW_SIZE", payload: size });
  const setMetricFieldPath = (path: string) =>
    dispatch({ type: "SET_METRIC_FIELD_PATH", payload: path });
  const setExposureType = (
    type: "asset_allocation" | "sector" | "geography" | "currency"
  ) => dispatch({ type: "SET_EXPOSURE_TYPE", payload: type });
  const setCombineVariants = (combine: boolean) =>
    dispatch({ type: "SET_COMBINE_VARIANTS", payload: combine });
  const setExposureDisplayMode = (
    mode: "weights" | "scaled_weights" | "values"
  ) => dispatch({ type: "SET_EXPOSURE_DISPLAY_MODE", payload: mode });
  const setXStatistic = (stat: string) =>
    dispatch({ type: "SET_X_STATISTIC", payload: stat });
  const setYStatistic = (stat: string) =>
    dispatch({ type: "SET_Y_STATISTIC", payload: stat });
  const setXLabel = (label: string) =>
    dispatch({ type: "SET_X_LABEL", payload: label });
  const setYLabel = (label: string) =>
    dispatch({ type: "SET_Y_LABEL", payload: label });
  const setColumns = (columns: StatsViewColumn[]) =>
    dispatch({ type: "SET_COLUMNS", payload: columns });
  const setShowBenchmarks = (show: boolean) =>
    dispatch({ type: "SET_SHOW_BENCHMARKS", payload: show });
  const setExtendIncompleteWithProxy = (enabled: boolean) =>
    dispatch({ type: "SET_EXTEND_INCOMPLETE_WITH_PROXY", payload: enabled });
  const setRiskFreeRate = (rate: number) =>
    dispatch({ type: "SET_RISK_FREE_RATE", payload: rate });

  // Bulk update multiple settings at once (useful for URL param loading)
  const setMultipleSettings = (settingsUpdate: Partial<ChartSettings>) =>
    dispatch({ type: "SET_MULTIPLE", payload: settingsUpdate });

  return {
    settings,
    setPeriod,
    setCustomDateRange,
    setDisplayMode,
    setRebaseValue,
    setFrequency,
    setStatistic,
    setWindowSize,
    setMetricFieldPath,
    setExposureType,
    setCombineVariants,
    setExposureDisplayMode,
    setXStatistic,
    setYStatistic,
    setXLabel,
    setYLabel,
    setColumns,
    setShowBenchmarks,
    setExtendIncompleteWithProxy,
    setRiskFreeRate,
    setMultipleSettings,
  };
};
